import type { FC, SVGProps } from "react";
import { PaperPencilOutlineIcon, SettingsOutlineIcon } from "@compono/ui-icons";

import type { IntegrationCategory } from "./types";

/**
 * Settings → E-signature, under the organisation prefix. Kept out of
 * `/settings/*`, which belongs to Organisation structure.
 */
export const ESIGNATURE_SETTINGS_PATH = "/e-signature";

/** Finishes a connection once the browser is back in its organisation. */
export const ESIGNATURE_COMPLETE_PATH = "/e-signature/complete";

/**
 * Where providers send the browser back. It has no organisation in it, because
 * it has to be registered with each provider as one fixed address.
 */
export const INTEGRATION_CALLBACK_PATH = "/integrations/callback";

export interface CategoryCopy {
  /** Under the category's heading. */
  description: string;
  /** How the notices name the category's provider, e.g. "e-signature provider". */
  providerNoun: string;
  /** What stops working while the category has no usable provider. */
  unavailable: string;
  /**
   * The disconnect warning for items still out with the provider: they stop
   * being tracked, so it says what that leaves the person to do by hand.
   */
  outstanding: (
    count: number,
    providerName: string,
  ) => { title: string; paragraphs: string[] };
  icon: FC<SVGProps<SVGSVGElement>>;
}

const CATEGORY_COPY: Record<IntegrationCategory, CategoryCopy> = {
  ESIGNATURE: {
    description:
      "Send offer documents for signature and file the signed copies with the offer.",
    providerNoun: "e-signature provider",
    unavailable: "Offers can't be sent for e-signature",
    outstanding: (count, providerName) => {
      const one = count === 1;
      return {
        title: one
          ? "1 envelope is still awaiting signature"
          : `${count} envelopes are still awaiting signature`,
        paragraphs: [
          `Compono will stop tracking ${one ? "it" : "them"}, and connecting ${providerName} again won't start tracking ${one ? "it" : "them"} again.`,
          one
            ? `The candidate can still sign it in ${providerName}, but the offer won't update when they do. You'll need to download the signed copy from ${providerName} and upload it to the offer's Document Repository yourself.`
            : `Candidates can still sign them in ${providerName}, but their offers won't update when they do. You'll need to download the signed copies from ${providerName} and upload them to each offer's Document Repository yourself.`,
        ],
      };
    },
    icon: PaperPencilOutlineIcon,
  },
};

/** For a category the server lists before this client knows its words. */
const genericCopy = (displayName: string): CategoryCopy => ({
  description: "",
  providerNoun: `${displayName} provider`,
  unavailable: `${displayName} isn't available`,
  outstanding: (count, providerName) => ({
    title:
      count === 1
        ? `1 item is still in progress with ${providerName}`
        : `${count} items are still in progress with ${providerName}`,
    paragraphs: [
      `Compono will stop tracking ${count === 1 ? "it" : "them"}, and connecting ${providerName} again won't start tracking ${count === 1 ? "it" : "them"} again.`,
    ],
  }),
  icon: SettingsOutlineIcon,
});

export const categoryCopy = (
  category: IntegrationCategory,
  displayName: string,
): CategoryCopy => CATEGORY_COPY[category] ?? genericCopy(displayName);
